import { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import { ArrowLeft, FileText, Mail, MapPin, Briefcase, GraduationCap, Award, Download, MessageSquare, BarChart, Edit2, ChevronRight, Plus, X, Check, Trash2, Building, ExternalLink } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import { API_BASE_URL, ENDPOINTS } from './config';
import { toast, Toaster } from 'react-hot-toast';
import ApplicantChatAccordion from './components/ApplicantChatAccordion';
import SourceAutocomplete from './components/SourceAutocomplete';

const JobNotesHeader = ({ jobNotes, jobId, onShowAll }) => {
  if (!jobNotes || jobNotes.length === 0) return null;
  
  const latestNote = jobNotes[0];
  
  // Handle potential undefined values
  const date = latestNote?.created_at ? new Date(latestNote.created_at).toLocaleDateString() : 'Unknown date';
  const recruiterName = latestNote?.recruiter_name || 'Unknown recruiter';
  const noteText = latestNote?.note || 'No note content';
  
  return (
    <div className="border rounded bg-white px-3 py-2 mx-4" style={{ minWidth: '300px', maxWidth: '500px' }}>
      <div className="d-flex align-items-center gap-3">
        <MessageSquare size={16} className="text-primary flex-shrink-0" />
        <div className="flex-grow-1 overflow-hidden">
          <small className="text-muted d-block">{date} • {recruiterName}</small>
          <p className="mb-0 text-truncate">{noteText}</p>
        </div>
        <button 
          className="btn btn-link btn-sm text-primary p-0 d-flex align-items-center gap-1 flex-shrink-0"
          onClick={onShowAll}
        >
          Show All
          <ChevronRight size={14} />
        </button>
      </div>
    </div>
  );
};

export default function CandidateDetails() {
  const { candidateId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [candidate, setCandidate] = useState(null);
  const [showResumeExtract, setShowResumeExtract] = useState(false);
  const [editingNote, setEditingNote] = useState(null);
  const [editedNoteText, setEditedNoteText] = useState('');
  const [updateLoading, setUpdateLoading] = useState(false);
  const [editingNoteType, setEditingNoteType] = useState(null);
  const [evaluatingMatch, setEvaluatingMatch] = useState(false);
  const [showAllNotes, setShowAllNotes] = useState(false);
  const [editingNoteInModal, setEditingNoteInModal] = useState(null);
  const [editedNoteTextInModal, setEditedNoteTextInModal] = useState('');
  const [showEditModal, setShowEditModal] = useState(false);
  const [editFormData, setEditFormData] = useState({
    location: '',
    experience_years: '',
    education: '',
    newSkill: '',
    skills_to_remove: [],
    skills_to_add: []
  });
  const [editLoading, setEditLoading] = useState(false);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusUpdateSuccess, setStatusUpdateSuccess] = useState(false);
  
  // Timeline state
  const [timeline, setTimeline] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [timelineError, setTimelineError] = useState(null);
  const [generatingTimeline, setGeneratingTimeline] = useState(false);
  
  const formatDuration = (startDate, endDate) => {
    if (!startDate) return null;
    const start = new Date(startDate);
    const end = endDate ? new Date(endDate) : new Date();
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return null;
    let totalMonths = (end.getFullYear() - start.getFullYear()) * 12;
    totalMonths += end.getMonth() - start.getMonth();
    if (end.getDate() < start.getDate()) {
      totalMonths -= 1;
    }
    if (totalMonths < 0) return null;
    const years = Math.floor(totalMonths / 12);
    const months = totalMonths % 12;
    if (years === 0 && months === 0) return '0m';
    const parts = [];
    if (years > 0) parts.push(`${years}y`);
    if (months > 0) parts.push(`${months}m`);
    return parts.join(' ');
  };
  

  
  // Company Research state
  const [companyResearch, setCompanyResearch] = useState(null);
  const [companyResearchLoading, setCompanyResearchLoading] = useState(false);
  const [companyResearchError, setCompanyResearchError] = useState(null);
  const [performingResearch, setPerformingResearch] = useState(false);
  
  // Company Research Edit state
  const [editingCompany, setEditingCompany] = useState(null);
  const [showEditCompanyModal, setShowEditCompanyModal] = useState(false);
  const [editCompanyData, setEditCompanyData] = useState({});
  const [savingCompanyEdit, setSavingCompanyEdit] = useState(false);
  
  // Company Research Details Modal state
  const [showCompanyDetailsModal, setShowCompanyDetailsModal] = useState(false);
  const [selectedCompanyDetails, setSelectedCompanyDetails] = useState(null);
  
  // Career Progression Research state
  const [careerProgression, setCareerProgression] = useState(null);
  const [careerProgressionLoading, setCareerProgressionLoading] = useState(false);
  const [careerProgressionError, setCareerProgressionError] = useState(null);
  const [performingCareerAnalysis, setPerformingCareerAnalysis] = useState(false);
  
  // Career Progression Details Modal state
  const [showCareerProgressionModal, setShowCareerProgressionModal] = useState(false);
  
  // New state variables for comparison
  const [comparing, setComparing] = useState(false);
  const [comparisonResults, setComparisonResults] = useState(null);
  const [otherCandidates, setOtherCandidates] = useState([]);
  const [selectedCandidateId, setSelectedCandidateId] = useState('');
  const [comparisonError, setComparisonError] = useState(null);

  const { jobId, jobTitle, applicationId, applicationStatus } = location.state || {};

  const [showAddNoteModal, setShowAddNoteModal] = useState(false);
  const [newNoteText, setNewNoteText] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  // Timeline functions
  const fetchTimeline = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setTimelineLoading(true);
    setTimelineError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const response = await fetch(`${API_BASE_URL}/timeline/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch timeline');
      
      setTimeline(data.data || []);
    } catch (error) {
      console.error('Error fetching timeline:', error);
      setTimelineError(error.message);
      toast.error('Failed to fetch timeline');
    } finally {
      setTimelineLoading(false);
    }
  };

  const generateTimeline = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setGeneratingTimeline(true);
    setTimelineError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const response = await fetch(`${API_BASE_URL}/timeline/generate/${applicationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to generate timeline');
      
      setTimeline(data.data.timeline_entries || []);
      toast.success('Timeline generated successfully');
    } catch (error) {
      console.error('Error generating timeline:', error);
      setTimelineError(error.message);
      toast.error('Failed to generate timeline: ' + error.message);
    } finally {
      setGeneratingTimeline(false);
    }
  };

  const deleteTimelineEntry = async (timelineId) => {
    if (!window.confirm('Are you sure you want to delete this timeline entry?')) {
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/timeline/entry/${timelineId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to delete timeline entry');

      // Refresh the timeline to get updated sequence numbers
      await fetchTimeline();
      toast.success('Timeline entry deleted successfully');
    } catch (error) {
      console.error('Error deleting timeline entry:', error);
      toast.error('Failed to delete timeline entry: ' + error.message);
    }
  };

  // Company Research functions
  const fetchCompanyResearch = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setCompanyResearchLoading(true);
    setCompanyResearchError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const response = await fetch(`${API_BASE_URL}/company-research/research/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 400 && data.message?.includes('No cached company research found')) {
          // No cached research found - this is expected, not an error
          setCompanyResearch(null);
          return;
        }
        throw new Error(data.error || 'Failed to fetch company research');
      }
      
      setCompanyResearch(data.data || null);
    } catch (error) {
      console.error('Error fetching company research:', error);
      setCompanyResearchError(error.message);
      toast.error('Failed to fetch company research');
    } finally {
      setCompanyResearchLoading(false);
    }
  };

  const performCompanyResearch = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setPerformingResearch(true);
    setCompanyResearchError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const response = await fetch(`${API_BASE_URL}/company-research/research/${applicationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to perform company research');
      
      setCompanyResearch(data.data || null);
      if (data.data?.cached) {
        toast.success('Company research retrieved from cache');
      } else {
        toast.success('Company research completed successfully');
      }
    } catch (error) {
      console.error('Error performing company research:', error);
      setCompanyResearchError(error.message);
      toast.error('Failed to perform company research: ' + error.message);
    } finally {
      setPerformingResearch(false);
    }
  };

  const refreshCompanyResearch = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setPerformingResearch(true);
    setCompanyResearchError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const response = await fetch(`${API_BASE_URL}/company-research/research/${applicationId}/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to refresh company research');
      
      setCompanyResearch(data.data || null);
      toast.success('Company research refreshed successfully');
    } catch (error) {
      console.error('Error refreshing company research:', error);
      setCompanyResearchError(error.message);
      toast.error('Failed to refresh company research: ' + error.message);
    } finally {
      setPerformingResearch(false);
    }
  };

  // Company Research Edit functions
  const handleEditCompany = (company, companyIndex) => {
    // Close the details modal if it's open
    if (showCompanyDetailsModal) {
      setShowCompanyDetailsModal(false);
      setSelectedCompanyDetails(null);
    }
    
    setEditingCompany(companyIndex);
    setEditCompanyData({
      company_name: company.company_name || '',
      overview: company.overview || '',
      industry: company.industry || '',
      size: company.size || '',
      founded: company.founded || '',
      headquarters: company.headquarters || '',
      products_services: company.products_services || '',
      recent_news: company.recent_news || '',
      financial_status: company.financial_status || '',
      culture: company.culture || '',
      notable_facts: company.notable_facts || '',
      technology_stack: company.technology_stack || '',
      research_notes: company.research_notes || '',
      research_confidence: company.research_confidence || 'Medium'
    });
    setShowEditCompanyModal(true);
  };

  const handleSaveCompanyEdit = async () => {
    if (!companyResearch?.research_id || editingCompany === null) return;
    
    setSavingCompanyEdit(true);
    try {
      // Create a copy of the current research data
      const updatedResearchData = { ...companyResearch };
      
      // Update the specific company data
      if (updatedResearchData.research_results && updatedResearchData.research_results[editingCompany]) {
        updatedResearchData.research_results[editingCompany] = {
          ...updatedResearchData.research_results[editingCompany],
          ...editCompanyData
        };
      }

      const response = await fetch(`${API_BASE_URL}/company-research/research/${companyResearch.research_id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          research_data: updatedResearchData
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update company research');

      // Update local state with the edited data
      setCompanyResearch(updatedResearchData);
      setShowEditCompanyModal(false);
      setEditingCompany(null);
      setEditCompanyData({});
      toast.success('Company research updated successfully');
    } catch (error) {
      console.error('Error updating company research:', error);
      toast.error('Failed to update company research: ' + error.message);
    } finally {
      setSavingCompanyEdit(false);
    }
  };

  const handleCancelCompanyEdit = () => {
    setShowEditCompanyModal(false);
    setEditingCompany(null);
    setEditCompanyData({});
  };

  // Company Research Details functions
  const handleShowCompanyDetails = (company) => {
    setSelectedCompanyDetails(company);
    setShowCompanyDetailsModal(true);
  };

  const handleCloseCompanyDetails = () => {
    setShowCompanyDetailsModal(false);
    setSelectedCompanyDetails(null);
  };

  // Career Progression Research functions
  const fetchCareerProgression = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setCareerProgressionLoading(true);
    setCareerProgressionError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const response = await fetch(`${API_BASE_URL}/career-progression/research/${applicationId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) {
        if (response.status === 404 && data.error?.includes('No career progression research found')) {
          // No cached research found - this is expected, not an error
          setCareerProgression(null);
          return;
        }
        throw new Error(data.error || 'Failed to fetch career progression research');
      }
      
      setCareerProgression(data.data || null);
    } catch (error) {
      console.error('Error fetching career progression research:', error);
      setCareerProgressionError(error.message);
      toast.error('Failed to fetch career progression research');
    } finally {
      setCareerProgressionLoading(false);
    }
  };

  const performCareerProgressionAnalysis = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setPerformingCareerAnalysis(true);
    setCareerProgressionError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      console.log("Application ID:", applicationId);
      const response = await fetch(`${API_BASE_URL}/career-progression/analyze/${applicationId}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to perform career progression analysis');
      
      setCareerProgression(data.data || null);
      if (data.data?.cached) {
        toast.success('Career progression analysis retrieved from cache');
      } else {
        toast.success('Career progression analysis completed successfully');
      }
    } catch (error) {
      console.error('Error performing career progression analysis:', error);
      setCareerProgressionError(error.message);
      toast.error('Failed to perform career progression analysis: ' + error.message);
    } finally {
      setPerformingCareerAnalysis(false);
    }
  };

  const refreshCareerProgressionAnalysis = async () => {
    if (!candidate?.applications?.find(app => app.job_id === jobId)?.application_id) return;
    
    setPerformingCareerAnalysis(true);
    setCareerProgressionError(null);
    
    try {
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const response = await fetch(`${API_BASE_URL}/career-progression/research/${applicationId}/refresh`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to refresh career progression analysis');
      
      setCareerProgression(data.data || null);
      toast.success('Career progression analysis refreshed successfully');
    } catch (error) {
      console.error('Error refreshing career progression analysis:', error);
      setCareerProgressionError(error.message);
      toast.error('Failed to refresh career progression analysis: ' + error.message);
    } finally {
      setPerformingCareerAnalysis(false);
    }
  };

  const fetchCandidateData = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/applicants/candidate/${candidateId}`, {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (!response.ok) {
        throw new Error('Failed to fetch candidate data');
      }
      
      const result = await response.json();
      setCandidate(result.data);
    } catch (error) {
      console.error('Error fetching candidate data:', error);
      toast.error('Failed to fetch candidate data');
    }
  };

  const handleAddNote = async () => {
    if (!newNoteText.trim()) return;
    
    setIsSaving(true);
    try {
      const response = await fetch(`${API_BASE_URL}/applicants/applicationnote`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          application_id: candidate.applications.find(app => app.job_id === jobId)?.application_id,
          note: newNoteText.trim()
        })
      });

      if (!response.ok) {
        throw new Error('Failed to add note');
      }

      // Refresh candidate data to show the new note
      await fetchCandidateData();
      setShowAddNoteModal(false);
      setNewNoteText('');
      toast.success('Note added successfully');
    } catch (error) {
      console.error('Error adding note:', error);
      toast.error('Failed to add note');
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    if (location.state?.from === 'search') {
      navigate('/jd', {
        state: {
          searchQuery: location.state.searchQuery,
          searchResults: location.state.searchResults
        }
      });
    } else if (location.state?.from === 'jobDetails') {
      navigate('/jd', {
        state: {
          showJobId: jobId,
          showApplicants: true,
          expandJobDetails: true
        },
        replace: true
      });
    } else if (jobId) {
      navigate('/jd', {
        state: {
          showJobId: jobId,
          showApplicants: true,
          expandJobDetails: true
        }
      });
    } else {
      navigate(-1);
    }
  };

  const handleEvaluateMatch = async () => {
    if (!jobId || !candidateId) return;
    
    setEvaluatingMatch(true);
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.EVALUATE_MATCH}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          jobseeker_id: candidateId,
          job_id: jobId
        })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to evaluate match');

      // Update the candidate state with new match data
      setCandidate(prev => ({
        ...prev,
        job_matches: [
          {
            job_id: jobId,
            job_title: jobTitle,
            matching_score: data.data.evaluation.matching_score,
            matching_text: data.data.evaluation.matching_text,
            non_matching_text: data.data.evaluation.non_matching_text
          },
          ...(prev.job_matches || [])
        ]
      }));
    } catch (err) {
      console.error('Error evaluating match:', err);
      setError(err.message);
    } finally {
      setEvaluatingMatch(false);
    }
  };

  useEffect(() => {
    const fetchCandidateDetails = async () => {
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(`${API_BASE_URL}${ENDPOINTS.CANDIDATE_DETAILS(candidateId)}`, {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to fetch candidate details');
        
        setCandidate(data.data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchCandidateDetails();
  }, [candidateId]);

  const getResumeFileName = (url) => {
    if (!url) return '';
    const parts = url.split('/');
    return parts[parts.length - 1];
  };

  const getResumeUrl = () => {
    return `${API_BASE_URL}${ENDPOINTS.DOWNLOAD_RESUME(candidateId)}`;
  };

  const handleResumeDownload = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(getResumeUrl(), {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (!response.ok) throw new Error('Failed to download resume');
      
      // Get the filename from the Content-Disposition header if available
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = getResumeFileName(candidate.resume_url);
      if (contentDisposition) {
        const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
        if (matches != null && matches[1]) {
          filename = matches[1].replace(/['"]/g, '');
        }
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error('Error downloading resume:', error);
      alert('Failed to download resume. Please try again.');
    }
  };

  const handleUpdateNote = async (noteId, jobId, type) => {
    try {
      setUpdateLoading(true);
      const endpoint = type === 'job' ? 'jobnote' : 'applicationnote';
      const response = await fetch(`${API_BASE_URL}/applicants/${endpoint}/${noteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ note: editedNoteText })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      // Update the local state to reflect the change
      setCandidate(prev => ({
        ...prev,
        applications: prev.applications.map(app => {
          if (app.job_id === jobId) {
            if (type === 'job') {
              return {
                ...app,
                job_notes: app.job_notes.map(note => 
                  note.note_id === noteId 
                    ? { ...note, note: editedNoteText, updated_at: new Date().toISOString() }
                    : note
                )
              };
            } else {
              return {
                ...app,
                application_notes: app.application_notes.map(note => 
                  note.note_id === noteId 
                    ? { ...note, note: editedNoteText, updated_at: new Date().toISOString() }
                    : note
                )
              };
            }
          }
          return app;
        })
      }));

      // Clear editing state
      setEditingNote(null);
      setEditedNoteText('');
      setEditingNoteType(null);
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note: ' + error.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdateNoteInModal = async (noteId, jobId, type) => {
    try {
      setUpdateLoading(true);
      const endpoint = type === 'job' ? 'jobnote' : 'applicationnote';
      const response = await fetch(`${API_BASE_URL}/applicants/${endpoint}/${noteId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({ note: editedNoteTextInModal })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error);

      // Update the local state to reflect the change
      setCandidate(prev => ({
        ...prev,
        applications: prev.applications.map(app => {
          if (app.job_id === jobId) {
            if (type === 'job') {
              return {
                ...app,
                job_notes: app.job_notes.map(note => 
                  note.note_id === noteId 
                    ? { ...note, note: editedNoteTextInModal, updated_at: new Date().toISOString() }
                    : note
                )
              };
            }
          }
          return app;
        })
      }));

      // Clear editing state
      setEditingNoteInModal(null);
      setEditedNoteTextInModal('');
    } catch (error) {
      console.error('Error updating note:', error);
      alert('Failed to update note: ' + error.message);
    } finally {
      setUpdateLoading(false);
    }
  };

  const handleUpdateJobseeker = async () => {
    try {
      setEditLoading(true);
      const token = localStorage.getItem('token');
      
      // Update jobseeker details
      const payload = {
        location: editFormData.location,
        experience_years: parseInt(editFormData.experience_years),
        education: editFormData.education,
        skills_to_remove: editFormData.skills_to_remove,
        skills_to_add: editFormData.skills_to_add || []
      };

      const response = await fetch(`${API_BASE_URL}/applicants/jobseeker/${candidateId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update jobseeker details');

      // Update source if it changed
      const applicationId = candidate.applications.find(app => app.job_id === jobId)?.application_id;
      const currentSource = candidate.applications[0]?.source;
      const newSelectedSource = editFormData.selected_source;
      
      // Check if source changed
      const sourceChanged = (
        (!currentSource && newSelectedSource) ||
        (currentSource && !newSelectedSource) ||
        (currentSource && newSelectedSource && currentSource.source_id !== newSelectedSource.source_id) ||
        (!currentSource && !newSelectedSource && editFormData.source_name.trim()) // New source created
      );

      if (sourceChanged && applicationId) {
        try {
          const sourceId = newSelectedSource?.source_id || null;
          const sourceResponse = await fetch(`${API_BASE_URL}${ENDPOINTS.APPLICATION_SOURCE(applicationId)}`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ source_id: sourceId })
          });

          if (!sourceResponse.ok) {
            const sourceError = await sourceResponse.json();
            console.error('Failed to update source:', sourceError);
            // Don't fail the entire operation, just log the error
          }
        } catch (sourceErr) {
          console.error('Error updating source:', sourceErr);
          // Don't fail the entire operation
        }
      }

      // Update the candidate state with new data
      setCandidate(prev => ({
        ...prev,
        location: data.data.location,
        experience_years: data.data.experience_years,
        education: data.data.education,
        skills: data.data.skills,
        applications: prev.applications.map(app => 
          app.application_id === applicationId && sourceChanged
            ? { ...app, source: newSelectedSource }
            : app
        )
      }));

      setShowEditModal(false);
      setShowSuccessMessage(true);
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } catch (err) {
      console.error('Error updating jobseeker details:', err);
      setError(err.message);
    } finally {
      setEditLoading(false);
    }
  };

  const handleOpenEditModal = () => {
    const currentSource = candidate.applications[0]?.source;
    setEditFormData({
      location: candidate.location || '',
      experience_years: candidate.experience_years?.toString() || '',
      education: candidate.education || '',
      source_name: currentSource?.source_name || '',
      selected_source: currentSource,
      newSkill: '',
      skills_to_remove: [],
      skills_to_add: []
    });
    setShowEditModal(true);
  };

  const handleAddSkill = () => {
    if (!editFormData.newSkill.trim()) return;
    
    const newSkill = editFormData.newSkill.trim();
    if (candidate.skills.includes(newSkill)) {
      setError('Skill already exists');
      return;
    }

    setEditFormData(prev => ({
      ...prev,
      skills_to_add: [...(prev.skills_to_add || []), newSkill],
      newSkill: ''
    }));
  };

  const handleStatusChange = async (newStatus) => {
    if (!applicationId) return;
    
    try {
      setUpdatingStatus(true);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.APPLICATION_STATUS(applicationId)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ status: newStatus })
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to update status');

      // Update the local state
      setCandidate(prev => ({
        ...prev,
        applications: prev.applications.map(app => 
          app.application_id === applicationId 
            ? { ...app, status: newStatus }
            : app
        )
      }));

      setStatusUpdateSuccess(true);
      // Hide success message after 3 seconds
      setTimeout(() => setStatusUpdateSuccess(false), 3000);
    } catch (err) {
      console.error('Error updating status:', err);
      setError(err.message);
    } finally {
      setUpdatingStatus(false);
    }
  };



  // Add new handler for fetching other candidates
  const fetchOtherCandidates = async () => {
    if (!jobId) return;
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/applicants/job/${jobId}`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      const data = await response.json();
      if (!response.ok) throw new Error(data.error || 'Failed to fetch other candidates');

      // Handle the new response structure with pagination
      const applications = data.data?.applications || [];
      
      // Filter out the current candidate and transform the data
      const candidates = applications
        .filter(app => app.candidate && app.candidate.id !== candidateId)
        .map(app => ({
          id: app.candidate.id,
          name: `${app.candidate.first_name} ${app.candidate.last_name}`,
          match_score: app.match_score || 0
        }));

      setOtherCandidates(candidates);
    } catch (err) {
      console.error('Error fetching other candidates:', err);
      setComparisonError(err.message);
      setOtherCandidates([]); // Reset to empty array on error
    }
  };

  // Add new handler for comparing candidates
  const handleCompareCandidates = async () => {
    if (!selectedCandidateId || !jobId) {
      console.log('Missing required data:', { selectedCandidateId, jobId });
      return;
    }
    
    try {
      setComparing(true);
      setComparisonError(null);
      const token = localStorage.getItem('token');
      
      // Log the request data
      const requestData = {
        jobseeker_id1: candidateId,
        jobseeker_id2: selectedCandidateId,
        job_id: jobId
      };
      console.log('Sending comparison request with data:', requestData);
      console.log('Authorization token present:', !!token);
      
      const response = await fetch(`${API_BASE_URL}${ENDPOINTS.COMPARE_CANDIDATES}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestData)
      });

      // Log the response status and headers
      console.log('Response status:', response.status);
      console.log('Response headers:', Object.fromEntries(response.headers.entries()));
      
      const data = await response.json();
      console.log('Response data:', data);
      
      if (!response.ok) {
        console.error('Comparison request failed:', data);
        throw new Error(data.error || 'Failed to compare candidates');
      }

      setComparisonResults(data.data);
    } catch (err) {
      console.error('Error comparing candidates:', {
        error: err,
        message: err.message,
        stack: err.stack
      });
      setComparisonError(err.message);
    } finally {
      setComparing(false);
    }
  };

  // Add useEffect to fetch other candidates when jobId is available
  useEffect(() => {
    if (jobId) {
      fetchOtherCandidates();
    }
  }, [jobId]);

  // Fetch timeline when candidate and jobId are available
  useEffect(() => {
    if (candidate && jobId) {
      fetchTimeline();
    }
  }, [candidate, jobId]);

  // Fetch company research when candidate and jobId are available
  useEffect(() => {
    if (candidate && jobId) {
      fetchCompanyResearch();
    }
  }, [candidate, jobId]);

  // Fetch career progression when candidate and jobId are available
  useEffect(() => {
    if (candidate && jobId) {
      fetchCareerProgression();
    }
  }, [candidate, jobId]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid">
      <Toaster position="top-right" />
      {/* Header */}
      <div className="bg-light border-bottom py-3">
        <div className="container">
          <div className="d-flex align-items-center">
            <button 
              className="btn btn-link text-decoration-none p-0 me-3"
              onClick={handleBack}
            >
              <ArrowLeft size={20} />
            </button>
            <div>
              <h1 className="h3 mb-0">
                {candidate.first_name} {candidate.last_name}
              </h1>
              {jobTitle && (
                <p className="text-muted mb-0">
                  Application for: {jobTitle}
                </p>
              )}
            </div>
            {candidate.applications?.map((app) => {
              if (app.job_id === jobId && app.job_notes?.length > 0) {
                return (
                  <JobNotesHeader 
                    key={app.job_id}
                    jobNotes={app.job_notes}
                    jobId={app.job_id}
                    onShowAll={() => setShowAllNotes(true)}
                  />
                );
              }
              return null;
            })}
            {applicationId && (
              <div className="ms-auto d-flex align-items-center">
                {statusUpdateSuccess && (
                  <div className="alert alert-success alert-dismissible fade show me-3 mb-0 py-2" role="alert">
                    Status updated successfully
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setStatusUpdateSuccess(false)}
                      aria-label="Close"
                    ></button>
                  </div>
                )}
                <select
                  className={`form-select ${updatingStatus ? 'disabled' : ''}`}
                  value={candidate.applications?.find(app => app.application_id === applicationId)?.status || 'applied'}
                  onChange={(e) => handleStatusChange(e.target.value)}
                  disabled={updatingStatus}
                  style={{ width: 'auto' }}
                >
                  <option value="applied">Applied</option>
                  <option value="interviewing">Interviewing</option>
                  <option value="offered">Offered</option>
                  <option value="rejected">Rejected</option>
                  <option value="withdrawn">Withdrawn</option>
                </select>
                {updatingStatus && (
                  <div className="spinner-border spinner-border-sm ms-2" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container my-4">
        <div className="row">
          {/* Left Column - Basic Info */}
          <div className="col-md-4">
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-start mb-4">
                  <h5 className="card-title mb-0">Contact Information</h5>
                  <button 
                    className="btn btn-link text-primary p-0"
                    onClick={handleOpenEditModal}
                  >
                    <Edit2 size={16} />
                  </button>
                </div>
                {showSuccessMessage && (
                  <div className="alert alert-success alert-dismissible fade show mb-3" role="alert">
                    <div className="d-flex align-items-center">
                      <Check size={16} className="me-2" />
                      <span>Candidate details updated successfully</span>
                    </div>
                  </div>
                )}
                <div className="d-flex mb-3">
                  <Mail size={20} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Email</div>
                    <div>{candidate.email}</div>
                  </div>
                </div>
                <div className="d-flex mb-3">
                  <MapPin size={20} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Location</div>
                    <div>{candidate.location}</div>
                  </div>
                </div>
                <div className="d-flex mb-3">
                  <Briefcase size={20} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Experience</div>
                    <div>{candidate.experience_years} years</div>
                  </div>
                </div>
                <div className="d-flex mb-3">
                  <GraduationCap size={20} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Education</div>
                    <div>{candidate.education}</div>
                  </div>
                </div>
                
                {/* Source Section */}
                <div className="d-flex mb-3">
                  <ExternalLink size={20} className="text-primary me-2" />
                  <div>
                    <div className="text-muted small">Source</div>
                    <div>
                      {candidate.applications[0]?.source ? (
                        <span>{candidate.applications[0].source.source_name}</span>
                      ) : (
                        <span className="text-muted fst-italic">No source specified</span>
                      )}
                    </div>
                  </div>
                </div>
                {candidate.resume_url && (
                  <>
                    <div className="d-flex mb-2">
                      <Download size={20} className="text-primary me-2" />
                      <div>
                        <div className="text-muted small">Resume</div>
                        <a 
                          href="#"
                          onClick={handleResumeDownload}
                          className="text-decoration-none"
                        >
                          {getResumeFileName(candidate.resume_url)}
                        </a>
                      </div>
                    </div>
                    {candidate.resume_extract && (
                      <div className="ms-4 ps-1">
                        <button
                          className="btn btn-link text-primary p-0 d-flex align-items-center gap-1"
                          onClick={() => setShowResumeExtract(true)}
                        >
                          <FileText size={14} />
                          <span>View Structured Format</span>
                        </button>
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            {/* Skills Card */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <Award size={20} className="text-primary me-2" />
                  Skills
                </h5>
                <div className="d-flex flex-wrap gap-2">
                  {candidate.skills.map((skill, index) => (
                    <span key={index} className="badge bg-light text-dark border">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Right Column - Resume Content and Job Matches */}
          <div className="col-md-8">
            {/* AI Chat Accordion */}
            {jobId && applicationId && (
              <ApplicantChatAccordion 
                applicationId={applicationId}
                jobId={jobId}
                candidateId={candidateId}
              />
            )}
            
            {/* Job Matches */}
            <div className="card mb-4">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <h5 className="card-title mb-0">
                    <BarChart size={20} className="text-primary me-2" />
                    Job Matches
                  </h5>
                  {jobId && candidate.job_matches?.length > 0 && (
                    <button
                      className="btn btn-outline-primary btn-sm"
                      onClick={handleEvaluateMatch}
                      disabled={evaluatingMatch}
                    >
                      {evaluatingMatch ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Evaluating Match...
                        </>
                      ) : (
                        <>
                          <BarChart size={16} className="me-2" />
                          Re-evaluate Match
                        </>
                      )}
                    </button>
                  )}
                </div>
                {candidate.job_matches?.length > 0 ? (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <h6 className="mb-0">{candidate.job_matches[0].job_title}</h6>
                      <div className={`badge ${
                        candidate.job_matches[0].matching_score >= 8 ? 'bg-success' :
                        candidate.job_matches[0].matching_score >= 6 ? 'bg-primary' :
                        candidate.job_matches[0].matching_score >= 4 ? 'bg-warning' :
                        'bg-danger'
                      }`}>
                        Score: {candidate.job_matches[0].matching_score}/10
                      </div>
                    </div>
                    <div className="mb-3">
                      <h6 className="text-success mb-2">Matching Qualities</h6>
                      <p className="mb-0">{candidate.job_matches[0].matching_text}</p>
                    </div>
                    <div className="mb-3">
                      <h6 className="text-danger mb-2">Areas of Mismatch</h6>
                      <p className="mb-0">{candidate.job_matches[0].non_matching_text}</p>
                    </div>
                  </div>
                ) : jobId ? (
                  <div className="text-center py-4">
                    <p className="text-muted mb-4">
                      Generate match evaluation to see how well this candidate matches the job requirements.
                    </p>
                    <button
                      className="btn btn-primary"
                      onClick={handleEvaluateMatch}
                      disabled={evaluatingMatch}
                    >
                      {evaluatingMatch ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                          Evaluating Match...
                        </>
                      ) : (
                        <>
                          <BarChart size={16} className="me-2" />
                          Generate Match Evaluation
                        </>
                      )}
                    </button>
                  </div>
                ) : (
                  <p className="text-muted text-center py-4 mb-0">
                    No job matches available. View this candidate from a job posting to generate a match evaluation.
                  </p>
                )}
              </div>
            </div>

            {/* Timeline Section */}
            {jobId && (
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <GraduationCap size={20} className="text-primary me-2" />
                      Timeline
                    </h5>
                    {timeline.length > 0 && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={generateTimeline}
                        disabled={generatingTimeline}
                      >
                        {generatingTimeline ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Regenerating...
                          </>
                        ) : (
                          'Regenerate Timeline'
                        )}
                      </button>
                    )}
                  </div>

                  {timelineError && (
                    <div className="alert alert-danger mb-3">
                      {timelineError}
                    </div>
                  )}

                  {timelineLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading timeline...</span>
                      </div>
                      <p className="text-muted mt-2 mb-0">Loading timeline...</p>
                    </div>
                  ) : timeline.length > 0 ? (
                    <div className="timeline-container">
                      {timeline.map((entry, index) => {
                        const durationLabel = formatDuration(entry.start_date, entry.end_date);
                        return (
                        <div key={entry.id || index} className="timeline-entry mb-4">
                          <div className="d-flex align-items-start">
                            <div className="timeline-marker me-3">
                              <div className={`timeline-dot ${
                                entry.entry_type === 'Work' ? 'bg-primary' :
                                entry.entry_type === 'Education' ? 'bg-success' :
                                'bg-warning'
                              }`}></div>
                              {index < timeline.length - 1 && <div className="timeline-line"></div>}
                            </div>
                            <div className="timeline-content flex-grow-1">
                              <div className="d-flex justify-content-between align-items-start mb-2">
                                <div>
                                  <span className={`badge ${
                                    entry.entry_type === 'Work' ? 'bg-primary' :
                                    entry.entry_type === 'Education' ? 'bg-success' :
                                    'bg-warning'
                                  } me-2`}>
                                    {entry.entry_type}
                                  </span>
                                  <span className="text-muted small">
                                    {entry.start_date ? new Date(entry.start_date).toLocaleDateString('en-US', { 
                                      year: 'numeric', 
                                      month: 'short' 
                                    }) : 'Unknown'} - {
                                      entry.end_date ? new Date(entry.end_date).toLocaleDateString('en-US', { 
                                        year: 'numeric', 
                                        month: 'short' 
                                      }) : 'Present'
                                    }
                                    {durationLabel ? ` • ${durationLabel}` : ''}
                                  </span>
                                </div>
                                <div className="d-flex align-items-center gap-2">
                                  <span className="badge bg-light text-primary border border-primary px-3 py-2" style={{ fontSize: '0.875rem', fontWeight: '600' }}>
                                    #{entry.sequence_number || index + 1}
                                  </span>
                                  <button
                                    className="btn btn-link text-danger p-1"
                                    onClick={() => deleteTimelineEntry(entry.id)}
                                    title="Delete timeline entry"
                                    style={{ 
                                      fontSize: '0.75rem',
                                      lineHeight: 1,
                                      opacity: 0.7,
                                      transition: 'opacity 0.2s'
                                    }}
                                    onMouseEnter={(e) => e.target.style.opacity = '1'}
                                    onMouseLeave={(e) => e.target.style.opacity = '0.7'}
                                  >
                                    <Trash2 size={12} />
                                  </button>
                                </div>
                              </div>
                              <p className="mb-0">{entry.description}</p>
                            </div>
                          </div>
                        </div>
                        );
                      })}
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <GraduationCap size={48} className="text-muted mb-3" />
                      <h6 className="text-muted mb-3">No Timeline Available</h6>
                      <p className="text-muted mb-4">
                        Generate a timeline to visualize this candidate's career progression and experience.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={generateTimeline}
                        disabled={generatingTimeline}
                      >
                        {generatingTimeline ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Generating Timeline...
                          </>
                        ) : (
                          <>
                            <GraduationCap size={16} className="me-2" />
                            Generate Timeline
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Company Research Section */}
            {jobId && (
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <Building size={20} className="text-primary me-2" />
                      Company Research
                    </h5>
                    {companyResearch && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={refreshCompanyResearch}
                        disabled={performingResearch}
                      >
                        {performingResearch ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Refreshing...
                          </>
                        ) : (
                          'Refresh Research'
                        )}
                      </button>
                    )}
                  </div>

                  {companyResearchError && (
                    <div className="alert alert-danger mb-3">
                      {companyResearchError}
                    </div>
                  )}

                  {companyResearchLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading company research...</span>
                      </div>
                      <p className="text-muted mt-2 mb-0">Loading company research...</p>
                    </div>
                                     ) : companyResearch ? (
                     <div className="company-research-results">
                       {/* Company Results */}
                       {companyResearch.research_results && companyResearch.research_results.length > 0 ? (
                         <>
                           <div className="row g-4 mb-4">
                             {companyResearch.research_results.map((company, index) => (
                                                                <div key={index} className="col-md-6">
                                   <div className="card h-100 border">
                                     <div className="card-body">
                                       <div className="d-flex justify-content-between align-items-start mb-3">
                                         <div className="d-flex align-items-center gap-2">
                                           <h6 className="card-title mb-0 text-primary">
                                             {company.company_name}
                                           </h6>
                                           <div className="d-flex gap-1">
                                             <button
                                               className="btn btn-link text-primary p-0"
                                               onClick={() => handleEditCompany(company, index)}
                                               title="Edit company research"
                                             >
                                               <Edit2 size={14} />
                                             </button>
                                           </div>
                                         </div>
                                         {company.research_confidence && (
                                           <span className={`badge ${
                                             company.research_confidence === 'High' ? 'bg-success' :
                                             company.research_confidence === 'Medium' ? 'bg-warning' :
                                             'bg-secondary'
                                           }`}>
                                           Confidence: {company.research_confidence}
                                           </span>
                                         )}
                                       </div>

                                       {company.error ? (
                                         <div className="text-danger small">
                                           <strong>Error:</strong> {company.error}
                                         </div>
                                       ) : (
                                         <>
                                           {company.overview && (
                                             <p className="card-text small mb-2">
                                               <strong>Overview:</strong> {company.overview.length > 150 ? `${company.overview.substring(0, 150)}...` : company.overview}
                                             </p>
                                           )}
                                           
                                           <div className="row g-2 mb-3">
                                             {company.industry && (
                                               <div className="col-6">
                                                 <small className="text-muted d-block">Industry</small>
                                                 <small>{company.industry}</small>
                                               </div>
                                             )}
                                             {company.size && (
                                               <div className="col-6">
                                                 <small className="text-muted d-block">Size</small>
                                                 <small>{company.size}</small>
                                               </div>
                                             )}
                                             {company.founded && (
                                               <div className="col-6">
                                                 <small className="text-muted d-block">Founded</small>
                                                 <small>{company.founded}</small>
                                               </div>
                                             )}
                                             {company.headquarters && (
                                               <div className="col-6">
                                                 <small className="text-muted d-block">Headquarters</small>
                                                 <small>{company.headquarters}</small>
                                               </div>
                                             )}
                                           </div>

                                           {company.timeline_context && (
                                             <div className="mt-3 pt-3 border-top">
                                               <small className="text-muted d-block mb-1">Work Context</small>
                                               <small className="text-dark">
                                                 {company.timeline_context.description}
                                               </small>
                                               {company.timeline_context.start_date && (
                                                 <div className="mt-1">
                                                   <small className="text-muted">
                                                     {new Date(company.timeline_context.start_date).toLocaleDateString('en-US', { 
                                                       year: 'numeric', 
                                                       month: 'short' 
                                                     })} - {
                                                       company.timeline_context.end_date ? 
                                                       new Date(company.timeline_context.end_date).toLocaleDateString('en-US', { 
                                                         year: 'numeric', 
                                                         month: 'short' 
                                                       }) : 'Present'
                                                     }
                                                   </small>
                                                 </div>
                                               )}
                                             </div>
                                           )}

                                           <div className="mt-3 pt-3 border-top">
                                             <button
                                               className="btn btn-outline-primary btn-sm w-100"
                                               onClick={() => handleShowCompanyDetails(company)}
                                             >
                                               <FileText size={14} className="me-1" />
                                               View Full Research
                                             </button>
                                           </div>
                                         </>
                                       )}
                                     </div>
                                   </div>
                                 </div>
                             ))}
                           </div>
                           
                           {/* Research Summary - moved to bottom */}
                           <div className="d-flex justify-content-center align-items-center gap-4 pt-3 border-top">
                             <small className="text-muted">
                               <strong>{companyResearch.companies_found || 0}</strong> companies found
                             </small>
                             <small className="text-muted">•</small>
                             <small className="text-muted">
                               <strong>{companyResearch.companies_researched || 0}</strong> researched
                             </small>
                             <small className="text-muted">•</small>
                             <small className="text-muted">
                               <strong>{companyResearch.work_entries_analyzed || 0}</strong> work entries analyzed
                             </small>
                             {companyResearch.research_date && (
                               <>
                                 <small className="text-muted">•</small>
                                 <small className="text-muted">
                                   Research date: <strong>{new Date(companyResearch.research_date).toLocaleDateString()}</strong>
                                 </small>
                               </>
                             )}
                             {companyResearch.cached && (
                               <>
                                 <small className="text-muted">•</small>
                                 <small className="text-warning">
                                   <strong>Cached data</strong>
                                 </small>
                               </>
                             )}
                           </div>
                         </>
                       ) : (
                         <div className="text-center py-3">
                           <p className="text-muted mb-0">No company research results available.</p>
                         </div>
                       )}
                     </div>
                  ) : (
                    <div className="text-center py-5">
                      <Building size={48} className="text-muted mb-3" />
                      <h6 className="text-muted mb-3">No Company Research Available</h6>
                      <p className="text-muted mb-4">
                        Research companies from this candidate's work history to get insights about their previous employers.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={performCompanyResearch}
                        disabled={performingResearch}
                      >
                        {performingResearch ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Researching Companies...
                          </>
                        ) : (
                          <>
                            <Building size={16} className="me-2" />
                            Research Companies
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Career Progression Research Section */}
            {jobId && (
              <div className="card mb-4">
                <div className="card-body">
                  <div className="d-flex justify-content-between align-items-center mb-4">
                    <h5 className="card-title mb-0">
                      <BarChart size={20} className="text-primary me-2" />
                      Career Progression Analysis
                    </h5>
                    {careerProgression && (
                      <button
                        className="btn btn-outline-primary btn-sm"
                        onClick={refreshCareerProgressionAnalysis}
                        disabled={performingCareerAnalysis}
                      >
                        {performingCareerAnalysis ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Refreshing...
                          </>
                        ) : (
                          'Refresh Analysis'
                        )}
                      </button>
                    )}
                  </div>

                  {careerProgressionError && (
                    <div className="alert alert-danger mb-3">
                      {careerProgressionError}
                    </div>
                  )}

                  {careerProgressionLoading ? (
                    <div className="text-center py-4">
                      <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading career progression analysis...</span>
                      </div>
                      <p className="text-muted mt-2 mb-0">Loading career progression analysis...</p>
                    </div>
                  ) : careerProgression ? (
                    <div className="career-progression-results">
                      {/* Career Progression Summary Preview */}
                      <div className="row g-4 mb-4">
                        {/* Overall Assessment */}
                        <div className="col-md-6">
                          <div className="card h-100 border">
                            <div className="card-body">
                              <h6 className="card-title text-primary mb-3">
                                Overall Assessment
                              </h6>
                              
                              <div className="row g-2 mb-3">
                                <div className="col-6">
                                  <small className="text-muted d-block">Career Trajectory</small>
                                                                     <small className="fw-medium">
                                     {careerProgression.analysis?.career_trajectory?.overall_direction || 'Unknown'}
                                   </small>
                                 </div>
                                 <div className="col-6">
                                   <small className="text-muted d-block">Progression Quality</small>
                                   <small className="fw-medium">
                                     {careerProgression.analysis?.career_trajectory?.progression_quality || 'Unknown'}
                                   </small>
                                 </div>
                                 <div className="col-6">
                                   <small className="text-muted d-block">Career Maturity</small>
                                   <small className="fw-medium">
                                     {careerProgression.analysis?.overall_assessment?.career_maturity || 'Unknown'}
                                   </small>
                                 </div>
                                 <div className="col-6">
                                   <small className="text-muted d-block">Risk Level</small>
                                   <small className={`fw-medium ${
                                     careerProgression.analysis?.overall_assessment?.risk_level === 'Low' ? 'text-success' :
                                     careerProgression.analysis?.overall_assessment?.risk_level === 'Medium' ? 'text-warning' :
                                     'text-danger'
                                   }`}>
                                     {careerProgression.analysis?.overall_assessment?.risk_level || 'Unknown'}
                                   </small>
                                </div>
                              </div>

                                                             {careerProgression.analysis?.current_application_fit && (
                                 <div className="mt-3 pt-3 border-top">
                                   <small className="text-muted d-block mb-1">Application Fit</small>
                                   <div className="d-flex justify-content-between align-items-center">
                                     <small className="fw-medium">
                                       {careerProgression.analysis.current_application_fit.role_alignment}
                                     </small>
                                     <span className={`badge ${
                                       careerProgression.analysis.current_application_fit.likelihood_of_success === 'Very High' || careerProgression.analysis.current_application_fit.likelihood_of_success === 'High' ? 'bg-success' :
                                       careerProgression.analysis.current_application_fit.likelihood_of_success === 'Medium' ? 'bg-warning' :
                                       'bg-danger'
                                     }`}>
                                       {careerProgression.analysis.current_application_fit.likelihood_of_success || 'Unknown'} Success Rate
                                     </span>
                                   </div>
                                 </div>
                               )}
                            </div>
                          </div>
                        </div>

                        {/* Key Insights */}
                        <div className="col-md-6">
                          <div className="card h-100 border">
                            <div className="card-body">
                              <h6 className="card-title text-primary mb-3">
                                Key Insights
                              </h6>
                              
                                                             {careerProgression.analysis?.strengths && careerProgression.analysis.strengths.length > 0 && (
                                 <div className="mb-3">
                                   <small className="text-success fw-medium d-block mb-2">Top Strengths</small>
                                   <ul className="list-unstyled mb-0">
                                     {careerProgression.analysis.strengths.slice(0, 2).map((strength, index) => (
                                       <li key={index} className="small mb-1">
                                         <span className="text-success me-1">•</span>
                                         {strength}
                                       </li>
                                     ))}
                                   </ul>
                                 </div>
                               )}
 
                               {careerProgression.analysis?.concerns && careerProgression.analysis.concerns.length > 0 && (
                                 <div className="mb-3">
                                   <small className="text-warning fw-medium d-block mb-2">Key Concerns</small>
                                   <ul className="list-unstyled mb-0">
                                     {careerProgression.analysis.concerns.slice(0, 2).map((concern, index) => (
                                       <li key={index} className="small mb-1">
                                         <span className="text-warning me-1">•</span>
                                         {concern}
                                       </li>
                                     ))}
                                   </ul>
                                 </div>
                               )}
 
                               {careerProgression.analysis?.overall_assessment?.summary && (
                                 <div className="mt-3 pt-3 border-top">
                                   <small className="text-muted d-block mb-1">Summary</small>
                                   <small className="text-dark">
                                     {careerProgression.analysis.overall_assessment.summary.length > 150 
                                       ? `${careerProgression.analysis.overall_assessment.summary.substring(0, 150)}...`
                                       : careerProgression.analysis.overall_assessment.summary
                                     }
                                   </small>
                                 </div>
                               )}
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Analysis Summary */}
                      <div className="d-flex justify-content-center align-items-center gap-4 pt-3 border-top">
                                                 <small className="text-muted">
                           <strong>{careerProgression.analysis?.metadata?.timeline_entries_analyzed || 0}</strong> timeline entries analyzed
                         </small>
                         <small className="text-muted">•</small>
                         <small className="text-muted">
                           <strong>{careerProgression.analysis?.role_progression?.length || 0}</strong> role progressions identified
                         </small>
                         <small className="text-muted">•</small>
                         <small className="text-muted">
                           <strong>{careerProgression.analysis?.career_gaps?.length || 0}</strong> career gaps detected
                         </small>
                         {careerProgression.analysis?.metadata?.analysis_date && (
                           <>
                             <small className="text-muted">•</small>
                             <small className="text-muted">
                               Analysis date: <strong>{new Date(careerProgression.analysis.metadata.analysis_date).toLocaleDateString()}</strong>
                             </small>
                           </>
                         )}
                        {careerProgression.cached && (
                          <>
                            <small className="text-muted">•</small>
                            <small className="text-warning">
                              <strong>Cached data</strong>
                            </small>
                          </>
                        )}
                      </div>

                      {/* View Full Analysis Button */}
                      <div className="mt-4 text-center">
                        <button
                          className="btn btn-primary"
                          onClick={() => setShowCareerProgressionModal(true)}
                        >
                          <FileText size={16} className="me-1" />
                          View Full Career Analysis
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      <BarChart size={48} className="text-muted mb-3" />
                      <h6 className="text-muted mb-3">No Career Progression Analysis Available</h6>
                      <p className="text-muted mb-4">
                        Analyze this candidate's career progression to get insights about their career trajectory, role progression, and fit for this position.
                      </p>
                      <button
                        className="btn btn-primary"
                        onClick={performCareerProgressionAnalysis}
                        disabled={performingCareerAnalysis}
                      >
                        {performingCareerAnalysis ? (
                          <>
                            <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                            Analyzing Career...
                          </>
                        ) : (
                          <>
                            <BarChart size={16} className="me-2" />
                            Analyze Career Progression
                          </>
                        )}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Application Notes */}
            {candidate.applications?.map((app) => (
              app.job_id === jobId && (
                <div key={app.application_id} className="card mb-4">
                  <div className="card-body">
                    <div className="d-flex justify-content-between align-items-center mb-4">
                      <h5 className="card-title mb-0">
                        <MessageSquare size={20} className="text-primary me-2" />
                        Application Notes
                      </h5>
                      <button
                        className="btn btn-primary btn-sm"
                        onClick={() => setShowAddNoteModal(true)}
                      >
                        <Plus size={16} className="me-1" />
                        Add Note
                      </button>
                    </div>
                    {app.application_notes.length > 0 ? (
                      <div className="timeline">
                        {app.application_notes.map((note, index) => (
                          <div 
                            key={index} 
                            className={`timeline-item mb-3 ${index < app.application_notes.length - 1 ? 'pb-3 border-bottom' : ''}`}
                          >
                            <div className="d-flex justify-content-between mb-2">
                              <small className="text-muted">{new Date(note.created_at).toLocaleDateString()}</small>
                              <small className="text-primary">{note.recruiter_name}</small>
                            </div>
                            {editingNote === note.note_id && editingNoteType === 'application' ? (
                              <div className="mb-2">
                                <textarea
                                  className="form-control mb-2"
                                  value={editedNoteText}
                                  onChange={(e) => setEditedNoteText(e.target.value)}
                                  rows="3"
                                />
                                <div className="d-flex gap-2">
                                  <button
                                    className="btn btn-primary btn-sm"
                                    onClick={() => handleUpdateNote(note.note_id, app.job_id, 'application')}
                                    disabled={updateLoading || !editedNoteText.trim()}
                                  >
                                    {updateLoading ? 'Saving...' : 'Save'}
                                  </button>
                                  <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => {
                                      setEditingNote(null);
                                      setEditedNoteText('');
                                      setEditingNoteType(null);
                                    }}
                                    disabled={updateLoading}
                                  >
                                    Cancel
                                  </button>
                                </div>
                              </div>
                            ) : (
                              <div className="d-flex justify-content-between align-items-start">
                                <p className="mb-0">{note.note}</p>
                                {note.can_edit && (
                                  <button
                                    className="btn btn-link btn-sm text-primary p-0 ms-2"
                                    onClick={() => {
                                      setEditingNote(note.note_id);
                                      setEditedNoteText(note.note);
                                      setEditingNoteType('application');
                                    }}
                                  >
                                    <Edit2 size={14} />
                                  </button>
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted mb-0">No application notes available</p>
                    )}
                  </div>
                </div>
              )
            ))}

            {/* Add Note Modal */}
            <div className={`modal fade ${showAddNoteModal ? 'show' : ''}`} 
                 style={{ display: showAddNoteModal ? 'block' : 'none' }}
                 tabIndex="-1">
              <div className="modal-dialog">
                <div className="modal-content">
                  <div className="modal-header">
                    <h5 className="modal-title">Add Application Note</h5>
                    <button
                      type="button"
                      className="btn-close"
                      onClick={() => {
                        setShowAddNoteModal(false);
                        setNewNoteText('');
                      }}
                    ></button>
                  </div>
                  <div className="modal-body">
                    <textarea
                      className="form-control"
                      rows="4"
                      value={newNoteText}
                      onChange={(e) => setNewNoteText(e.target.value)}
                      placeholder="Enter your note here..."
                    />
                  </div>
                  <div className="modal-footer">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={() => {
                        setShowAddNoteModal(false);
                        setNewNoteText('');
                      }}
                      disabled={isSaving}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary"
                      onClick={handleAddNote}
                      disabled={isSaving || !newNoteText.trim()}
                    >
                      {isSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
            {showAddNoteModal && (
              <div className="modal-backdrop fade show"></div>
            )}

            {/* Resume Text Card */}
            {candidate.resume_text && candidate.resume_text.trim() && (
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-4">Resume Content</h5>
                  <pre className="resume-text" style={{ whiteSpace: 'pre-wrap', fontFamily: 'inherit' }}>
                    {candidate.resume_text}
                  </pre>
                </div>
              </div>
            )}

            {/* Candidate Comparison Section */}
            {jobId && (
              <div className="card mb-4">
                <div className="card-body">
                  <h5 className="card-title mb-4">
                    <BarChart size={20} className="text-primary me-2" />
                    Compare with Other Candidates
                  </h5>
                  
                  {comparisonError && (
                    <div className="alert alert-danger mb-3">
                      {comparisonError}
                    </div>
                  )}

                  {otherCandidates.length > 0 ? (
                    <>
                      <div className="d-flex gap-3 mb-4">
                        <select
                          className="form-select"
                          value={selectedCandidateId}
                          onChange={(e) => setSelectedCandidateId(e.target.value)}
                          disabled={comparing}
                        >
                          <option value="">Select a candidate to compare</option>
                          {otherCandidates.map(candidate => (
                            <option key={candidate.id} value={candidate.id}>
                              {candidate.name} (Match Score: {candidate.match_score || 'N/A'})
                            </option>
                          ))}
                        </select>
                        <button
                          className="btn btn-primary"
                          onClick={handleCompareCandidates}
                          disabled={!selectedCandidateId || comparing}
                        >
                          {comparing ? (
                            <>
                              <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                              Comparing...
                            </>
                          ) : (
                            'Compare Candidates'
                          )}
                        </button>
                      </div>

                      {comparisonResults && (
                        <div className="comparison-results">
                          <div className="row g-4">
                            {/* Strengths Section */}
                            <div className="col-12">
                              <div className="row">
                                <div className="col-md-6">
                                  <h6 className="card-title text-primary mb-3">
                                    {comparisonResults.candidate1.name}
                                    <span className="badge bg-primary ms-2">
                                      Score: {comparisonResults.candidate1.overall_score}/10
                                    </span>
                                  </h6>
                                </div>
                                <div className="col-md-6">
                                  <h6 className="card-title text-primary mb-3">
                                    {comparisonResults.candidate2.name}
                                    <span className="badge bg-primary ms-2">
                                      Score: {comparisonResults.candidate2.overall_score}/10
                                    </span>
                                  </h6>
                                </div>
                              </div>
                            </div>
                            
                            {/* Strengths Section */}
                            <div className="col-12">
                              <h6 className="text-success mb-3">Strengths Comparison</h6>
                              <div className="row">
                                <div className="col-md-6">
                                  <ul className="list-unstyled">
                                    {comparisonResults.candidate1.strengths.map((strength, index) => (
                                      <li key={index} className="mb-2">
                                        <Check size={16} className="text-success me-2" />
                                        {strength}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="col-md-6">
                                  <ul className="list-unstyled">
                                    {comparisonResults.candidate2.strengths.map((strength, index) => (
                                      <li key={index} className="mb-2">
                                        <Check size={16} className="text-success me-2" />
                                        {strength}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Weaknesses Section */}
                            <div className="col-12">
                              <h6 className="text-danger mb-3">Weaknesses Comparison</h6>
                              <div className="row">
                                <div className="col-md-6">
                                  <ul className="list-unstyled">
                                    {comparisonResults.candidate1.weaknesses.map((weakness, index) => (
                                      <li key={index} className="mb-2">
                                        <X size={16} className="text-danger me-2" />
                                        {weakness}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                                <div className="col-md-6">
                                  <ul className="list-unstyled">
                                    {comparisonResults.candidate2.weaknesses.map((weakness, index) => (
                                      <li key={index} className="mb-2">
                                        <X size={16} className="text-danger me-2" />
                                        {weakness}
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              </div>
                            </div>

                            {/* Fit Analysis Section */}
                            <div className="col-12">
                              <h6 className="text-primary mb-3">Fit Analysis Comparison</h6>
                              <div className="row">
                                <div className="col-md-6">
                                  <p className="mb-0">{comparisonResults.candidate1.fit_analysis}</p>
                                </div>
                                <div className="col-md-6">
                                  <p className="mb-0">{comparisonResults.candidate2.fit_analysis}</p>
                                </div>
                              </div>
                            </div>
                          </div>
                          
                          <div className="card mt-4">
                            <div className="card-body">
                              <h6 className="card-title text-primary mb-3">Comparison Summary</h6>
                              <p className="mb-0">{comparisonResults.comparison_summary}</p>
                            </div>
                          </div>

                          <div className="card mt-4">
                            <div className="card-body">
                              <h6 className="card-title text-primary mb-3">Recommendation</h6>
                              <p className="mb-0">{comparisonResults.recommendation}</p>
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  ) : (
                    <p className="text-muted mb-0">
                      No other candidates available for comparison.
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Resume Extract Modal */}
      {showResumeExtract && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Structured Resume</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => setShowResumeExtract(false)}
                ></button>
              </div>
              <div className="modal-body">
                <div className="resume-extract markdown-content">
                  <ReactMarkdown>{candidate.resume_extract || ''}</ReactMarkdown>
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowResumeExtract(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notes Modal */}
      {showAllNotes && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Job Notes History</h5>
                <button 
                  type="button" 
                  className="btn-close" 
                  onClick={() => {
                    setShowAllNotes(false);
                    setEditingNoteInModal(null);
                    setEditedNoteTextInModal('');
                  }}
                ></button>
              </div>
              <div className="modal-body">
                {candidate.applications?.map((app) => (
                  app.job_id === jobId && (
                    <div className="timeline" key={app.job_id}>
                      {app.job_notes.map((note, index) => (
                        <div 
                          key={index} 
                          className={`timeline-item mb-3 ${index < app.job_notes.length - 1 ? 'pb-3 border-bottom' : ''}`}
                        >
                          <div className="d-flex justify-content-between mb-2">
                            <small className="text-muted">{new Date(note.created_at).toLocaleDateString()}</small>
                            <small className="text-primary">{note.recruiter_name}</small>
                          </div>
                          {editingNoteInModal === note.note_id ? (
                            <div className="mb-2">
                              <textarea
                                className="form-control mb-2"
                                value={editedNoteTextInModal}
                                onChange={(e) => setEditedNoteTextInModal(e.target.value)}
                                rows="3"
                                autoFocus
                              />
                              <div className="d-flex gap-2 justify-content-end">
                                <button
                                  className="btn btn-primary btn-sm"
                                  onClick={() => handleUpdateNoteInModal(note.note_id, app.job_id, 'job')}
                                  disabled={updateLoading || !editedNoteTextInModal.trim()}
                                >
                                  {updateLoading ? 'Saving...' : 'Save'}
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => {
                                    setEditingNoteInModal(null);
                                    setEditedNoteTextInModal('');
                                  }}
                                  disabled={updateLoading}
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <div className="d-flex justify-content-between align-items-start">
                              <p className="mb-0">{note.note}</p>
                              {note.can_edit && (
                                <button
                                  className="btn btn-link btn-sm text-primary p-0 ms-2"
                                  onClick={() => {
                                    setEditingNoteInModal(note.note_id);
                                    setEditedNoteTextInModal(note.note);
                                  }}
                                >
                                  <Edit2 size={14} />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )
                ))}
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => {
                    setShowAllNotes(false);
                    setEditingNoteInModal(null);
                    setEditedNoteTextInModal('');
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Add Edit Modal */}
      {showEditModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Candidate Details</h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowEditModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); handleUpdateJobseeker(); }}>
                  <div className="mb-3">
                    <label className="form-label">Location</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.location}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, location: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Experience (years)</label>
                    <input
                      type="number"
                      className="form-control"
                      min="0"
                      value={editFormData.experience_years}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, experience_years: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Education</label>
                    <input
                      type="text"
                      className="form-control"
                      value={editFormData.education}
                      onChange={(e) => setEditFormData(prev => ({ ...prev, education: e.target.value }))}
                    />
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Source</label>
                    <SourceAutocomplete
                      value={editFormData.source_name}
                      onChange={(value) => {
                        setEditFormData(prev => ({
                          ...prev,
                          source_name: value,
                          selected_source: prev.selected_source?.source_name === value ? prev.selected_source : null
                        }));
                      }}
                      onSourceSelect={(source) => {
                        setEditFormData(prev => ({
                          ...prev,
                          source_name: source.source_name,
                          selected_source: source
                        }));
                      }}
                      placeholder="Type to search sources like 'LinkedIn', 'Naukri', etc."
                    />
                    <small className="form-text text-muted">
                      Start typing to search existing sources or create a new one. Leave empty to remove source.
                    </small>
                  </div>
                  <div className="mb-3">
                    <label className="form-label">Skills</label>
                    <div className="d-flex flex-wrap gap-2 mb-2">
                      {candidate.skills.map((skill, index) => (
                        <span 
                          key={index} 
                          className={`badge ${
                            editFormData.skills_to_remove.includes(skill)
                              ? 'bg-danger'
                              : 'bg-light text-dark border'
                          }`}
                          style={{ cursor: 'pointer' }}
                          onClick={() => {
                            setEditFormData(prev => ({
                              ...prev,
                              skills_to_remove: prev.skills_to_remove.includes(skill)
                                ? prev.skills_to_remove.filter(s => s !== skill)
                                : [...prev.skills_to_remove, skill]
                            }));
                          }}
                        >
                          {skill}
                          {editFormData.skills_to_remove.includes(skill) ? (
                            <Plus size={14} className="ms-1" style={{ transform: 'rotate(45deg)' }} />
                          ) : (
                            <X size={14} className="ms-1" />
                          )}
                        </span>
                      ))}
                      {editFormData.skills_to_add?.map((skill, index) => (
                        <span 
                          key={`new-${index}`}
                          className="badge bg-success text-white"
                        >
                          {skill}
                          <Plus size={14} className="ms-1" />
                        </span>
                      ))}
                    </div>
                    <div className="d-flex gap-2 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add new skill"
                        value={editFormData.newSkill}
                        onChange={(e) => setEditFormData(prev => ({ ...prev, newSkill: e.target.value }))}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddSkill();
                          }
                        }}
                      />
                      <button
                        className="btn btn-primary"
                        onClick={handleAddSkill}
                        disabled={!editFormData.newSkill.trim()}
                      >
                        Add
                      </button>
                    </div>
                    <small className="text-muted d-block">
                      Click on existing skills to mark them for removal (shown in red)
                    </small>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => setShowEditModal(false)}
                  disabled={editLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleUpdateJobseeker}
                  disabled={editLoading}
                >
                  {editLoading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
              )}

      {/* Company Research Edit Modal */}
      {showEditCompanyModal && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Edit Company Research</h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={handleCancelCompanyEdit}
                  disabled={savingCompanyEdit}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={(e) => { e.preventDefault(); handleSaveCompanyEdit(); }}>
                  <div className="row g-3">
                    <div className="col-md-6">
                      <label className="form-label">Company Name</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editCompanyData.company_name || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, company_name: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Research Confidence</label>
                      <select
                        className="form-select"
                        value={editCompanyData.research_confidence || 'Medium'}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, research_confidence: e.target.value }))}
                      >
                        <option value="High">High</option>
                        <option value="Medium">Medium</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div className="col-12">
                      <label className="form-label">Overview</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        value={editCompanyData.overview || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, overview: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Industry</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editCompanyData.industry || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, industry: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Company Size</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editCompanyData.size || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, size: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Founded</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editCompanyData.founded || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, founded: e.target.value }))}
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">Headquarters</label>
                      <input
                        type="text"
                        className="form-control"
                        value={editCompanyData.headquarters || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, headquarters: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Products & Services</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={editCompanyData.products_services || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, products_services: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Recent News</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={editCompanyData.recent_news || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, recent_news: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Financial Status</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={editCompanyData.financial_status || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, financial_status: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Company Culture</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={editCompanyData.culture || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, culture: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Technology Stack</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={editCompanyData.technology_stack || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, technology_stack: e.target.value }))}
                        placeholder="Common tools, frameworks, languages, or platforms used by the company"
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Notable Facts</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={editCompanyData.notable_facts || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, notable_facts: e.target.value }))}
                      />
                    </div>
                    <div className="col-12">
                      <label className="form-label">Research Notes</label>
                      <textarea
                        className="form-control"
                        rows="2"
                        value={editCompanyData.research_notes || ''}
                        onChange={(e) => setEditCompanyData(prev => ({ ...prev, research_notes: e.target.value }))}
                        placeholder="Notes about data quality, limitations, or research methodology"
                      />
                    </div>
                  </div>
                </form>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCancelCompanyEdit}
                  disabled={savingCompanyEdit}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleSaveCompanyEdit}
                  disabled={savingCompanyEdit}
                >
                  {savingCompanyEdit ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                      Saving...
                    </>
                  ) : (
                    'Save Changes'
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Company Research Details Modal */}
      {showCompanyDetailsModal && selectedCompanyDetails && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <Building size={20} className="text-primary me-2" />
                  {selectedCompanyDetails.company_name} - Company Research
                </h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={handleCloseCompanyDetails}
                ></button>
              </div>
              <div className="modal-body">
                <div className="company-details">
                  {/* Company Header Info */}
                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <div className="d-flex align-items-center gap-2 mb-2">
                        <h6 className="mb-0 text-primary">{selectedCompanyDetails.company_name}</h6>
                        {selectedCompanyDetails.research_confidence && (
                          <span className={`badge ${
                            selectedCompanyDetails.research_confidence === 'High' ? 'bg-success' :
                            selectedCompanyDetails.research_confidence === 'Medium' ? 'bg-warning' :
                            'bg-secondary'
                          }`}>
                            Confidence: {selectedCompanyDetails.research_confidence}
                          </span>
                        )}
                      </div>
                      {selectedCompanyDetails.timeline_context && (
                        <div className="mt-2 p-2 bg-light rounded">
                          <small className="text-muted d-block mb-1"><strong>Work Context</strong></small>
                          <small className="text-dark d-block">
                            {selectedCompanyDetails.timeline_context.description}
                          </small>
                          {selectedCompanyDetails.timeline_context.start_date && (
                            <small className="text-muted d-block mt-1">
                              {new Date(selectedCompanyDetails.timeline_context.start_date).toLocaleDateString('en-US', { 
                                year: 'numeric', 
                                month: 'short' 
                              })} - {
                                selectedCompanyDetails.timeline_context.end_date ? 
                                new Date(selectedCompanyDetails.timeline_context.end_date).toLocaleDateString('en-US', { 
                                  year: 'numeric', 
                                  month: 'short' 
                                }) : 'Present'
                              }
                            </small>
                          )}
                        </div>
                      )}
                    </div>
                    <div className="col-md-6">
                      <div className="row g-2">
                        {selectedCompanyDetails.industry && (
                          <div className="col-6">
                            <small className="text-muted d-block">Industry</small>
                            <small className="fw-medium">{selectedCompanyDetails.industry}</small>
                          </div>
                        )}
                        {selectedCompanyDetails.size && (
                          <div className="col-6">
                            <small className="text-muted d-block">Size</small>
                            <small className="fw-medium">{selectedCompanyDetails.size}</small>
                          </div>
                        )}
                        {selectedCompanyDetails.founded && (
                          <div className="col-6">
                            <small className="text-muted d-block">Founded</small>
                            <small className="fw-medium">{selectedCompanyDetails.founded}</small>
                          </div>
                        )}
                        {selectedCompanyDetails.headquarters && (
                          <div className="col-6">
                            <small className="text-muted d-block">Headquarters</small>
                            <small className="fw-medium">{selectedCompanyDetails.headquarters}</small>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Detailed Information Sections */}
                  <div className="row g-4">
                    {selectedCompanyDetails.overview && (
                      <div className="col-12">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-2">
                              <Briefcase size={16} className="me-1" />
                              Company Overview
                            </h6>
                            <p className="mb-0">{selectedCompanyDetails.overview}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedCompanyDetails.products_services && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-2">
                              <Award size={16} className="me-1" />
                              Products & Services
                            </h6>
                            <p className="mb-0">{selectedCompanyDetails.products_services}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedCompanyDetails.recent_news && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-2">
                              <MessageSquare size={16} className="me-1" />
                              Recent News
                            </h6>
                            <p className="mb-0">{selectedCompanyDetails.recent_news}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedCompanyDetails.financial_status && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-2">
                              <BarChart size={16} className="me-1" />
                              Financial Status
                            </h6>
                            <p className="mb-0">{selectedCompanyDetails.financial_status}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedCompanyDetails.culture && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-2">
                              <GraduationCap size={16} className="me-1" />
                              Company Culture
                            </h6>
                            <p className="mb-0">{selectedCompanyDetails.culture}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedCompanyDetails.technology_stack && typeof selectedCompanyDetails.technology_stack === 'string' && selectedCompanyDetails.technology_stack.trim() && (
                      <div className="col-12">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-2">
                              <Award size={16} className="me-1" />
                              Technology Stack
                            </h6>
                            <div className="technology-stack-content">
                              <ReactMarkdown>{selectedCompanyDetails.technology_stack}</ReactMarkdown>
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedCompanyDetails.notable_facts && (
                      <div className="col-12">
                        <div className="card border-0 bg-light">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-2">
                              <FileText size={16} className="me-1" />
                              Notable Facts
                            </h6>
                            <p className="mb-0">{selectedCompanyDetails.notable_facts}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {selectedCompanyDetails.research_notes && selectedCompanyDetails.research_notes.trim() && (
                      <div className="col-12">
                        <div className="card border-0 bg-warning bg-opacity-10 border-warning">
                          <div className="card-body">
                            <h6 className="card-title text-warning mb-2">
                              <MessageSquare size={16} className="me-1" />
                              Research Notes
                            </h6>
                            <p className="mb-0 text-dark">{selectedCompanyDetails.research_notes}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => handleEditCompany(selectedCompanyDetails, companyResearch.research_results.indexOf(selectedCompanyDetails))}
                >
                  <Edit2 size={16} className="me-1" />
                  Edit Research
                </button>
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={handleCloseCompanyDetails}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Career Progression Details Modal */}
      {showCareerProgressionModal && careerProgression && (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <BarChart size={20} className="text-primary me-2" />
                  Career Progression Analysis - {candidate.first_name} {candidate.last_name}
                </h5>
                <button 
                  type="button" 
                  className="btn-close"
                  onClick={() => setShowCareerProgressionModal(false)}
                ></button>
              </div>
              <div className="modal-body" style={{ maxHeight: '80vh', overflowY: 'auto' }}>
                <div className="career-progression-details">
                                     {/* Career Trajectory */}
                   {careerProgression.career_trajectory && (
                    <div className="card border-0 bg-light mb-4">
                      <div className="card-body">
                        <h6 className="card-title text-primary mb-3">
                          <ArrowLeft size={16} className="me-1" />
                          Career Trajectory
                        </h6>
                        <div className="row g-3">
                          <div className="col-md-3">
                            <small className="text-muted d-block">Direction</small>
                                                         <span className={`badge ${
                               careerProgression.career_trajectory.overall_direction === 'Upward' ? 'bg-success' :
                               careerProgression.career_trajectory.overall_direction === 'Lateral' ? 'bg-primary' :
                               'bg-warning'
                             }`}>
                               {careerProgression.career_trajectory.overall_direction}
                             </span>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Quality</small>
                            <span className={`badge ${
                              careerProgression.analysis.career_trajectory.progression_quality === 'Excellent' || careerProgression.analysis.career_trajectory.progression_quality === 'Good' ? 'bg-success' :
                              careerProgression.analysis.career_trajectory.progression_quality === 'Average' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {careerProgression.analysis.career_trajectory.progression_quality}
                            </span>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Acceleration</small>
                            <span className={`badge ${
                              careerProgression.analysis.career_trajectory.career_acceleration === 'High' ? 'bg-success' :
                              careerProgression.analysis.career_trajectory.career_acceleration === 'Medium' ? 'bg-primary' :
                              'bg-secondary'
                            }`}>
                              {careerProgression.analysis.career_trajectory.career_acceleration}
                            </span>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Summary</small>
                            <small>{careerProgression.analysis.career_trajectory.summary}</small>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Role Progression */}
                  {careerProgression.analysis?.role_progression && careerProgression.analysis.role_progression.length > 0 && (
                    <div className="card border-0 bg-light mb-4">
                      <div className="card-body">
                        <h6 className="card-title text-primary mb-3">
                          <Briefcase size={16} className="me-1" />
                          Role Progression Timeline
                        </h6>
                        <div className="row g-3">
                          {careerProgression.analysis.role_progression.map((role, index) => (
                            <div key={index} className="col-md-6">
                              <div className="border rounded p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <h6 className="mb-1">{role.position}</h6>
                                    <small className="text-muted">{role.company} • {role.duration}</small>
                                  </div>
                                  <div className="d-flex gap-1">
                                    <span className={`badge ${
                                      role.level === 'Executive' || role.level === 'Lead' ? 'bg-success' :
                                      role.level === 'Senior' || role.level === 'Mid' ? 'bg-primary' :
                                      'bg-secondary'
                                    }`}>
                                      {role.level}
                                    </span>
                                    <span className={`badge ${
                                      role.progression_type === 'Promotion' || role.progression_type === 'Step-up' ? 'bg-success' :
                                      role.progression_type === 'Lateral' ? 'bg-primary' :
                                      'bg-warning'
                                    }`}>
                                      {role.progression_type}
                                    </span>
                                  </div>
                                </div>
                                <div className="d-flex align-items-center gap-2 mb-2">
                                  {role.career_enhancing ? (
                                    <span className="badge bg-success">Career Enhancing</span>
                                  ) : (
                                    <span className="badge bg-warning">Not Career Enhancing</span>
                                  )}
                                </div>
                                <p className="small mb-0">{role.analysis}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Career Gaps */}
                  {careerProgression.analysis?.career_gaps && careerProgression.analysis.career_gaps.length > 0 && (
                    <div className="card border-0 bg-light mb-4">
                      <div className="card-body">
                        <h6 className="card-title text-warning mb-3">
                          <X size={16} className="me-1" />
                          Career Gaps Analysis
                        </h6>
                        <div className="row g-3">
                          {careerProgression.analysis.career_gaps.map((gap, index) => (
                            <div key={index} className="col-md-6">
                              <div className="border rounded p-3">
                                <div className="d-flex justify-content-between align-items-start mb-2">
                                  <div>
                                    <h6 className="mb-1">{gap.period}</h6>
                                    <small className="text-muted">{gap.duration_months} months</small>
                                  </div>
                                  <span className={`badge ${
                                    gap.impact_on_career === 'Positive' ? 'bg-success' :
                                    gap.impact_on_career === 'Neutral' ? 'bg-secondary' :
                                    'bg-danger'
                                  }`}>
                                    {gap.impact_on_career}
                                  </span>
                                </div>
                                <div className="mb-2">
                                  <small className="text-muted d-block">Likely Reason</small>
                                  <small className="fw-medium">{gap.likely_reason}</small>
                                </div>
                                <p className="small mb-0">{gap.analysis}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Company Progression & Skills Development */}
                  <div className="row g-4 mb-4">
                    {/* Company Progression */}
                    {careerProgression.analysis?.company_progression && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light h-100">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-3">
                              <Building size={16} className="me-1" />
                              Company Progression
                            </h6>
                            <div className="row g-2 mb-3">
                              <div className="col-12">
                                <small className="text-muted d-block">Quality Trend</small>
                                <span className={`badge ${
                                  careerProgression.analysis.company_progression.company_quality_trend === 'Improving' ? 'bg-success' :
                                  careerProgression.analysis.company_progression.company_quality_trend === 'Stable' ? 'bg-primary' :
                                  'bg-warning'
                                }`}>
                                  {careerProgression.analysis.company_progression.company_quality_trend}
                                </span>
                              </div>
                              <div className="col-12">
                                <small className="text-muted d-block">Size Trend</small>
                                <span className="badge bg-secondary">
                                  {careerProgression.analysis.company_progression.company_size_trend}
                                </span>
                              </div>
                              <div className="col-12">
                                <small className="text-muted d-block">Industry Focus</small>
                                <span className="badge bg-info">
                                  {careerProgression.analysis.company_progression.industry_focus}
                                </span>
                              </div>
                            </div>
                            <p className="small mb-0">{careerProgression.analysis.company_progression.analysis}</p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Skills Development */}
                    {careerProgression.analysis?.skills_development && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light h-100">
                          <div className="card-body">
                            <h6 className="card-title text-primary mb-3">
                              <Award size={16} className="me-1" />
                              Skills Development
                            </h6>
                            <div className="row g-2 mb-3">
                              <div className="col-12">
                                <small className="text-muted d-block">Technical Growth</small>
                                <span className={`badge ${
                                  careerProgression.analysis.skills_development.technical_growth === 'Strong' ? 'bg-success' :
                                  careerProgression.analysis.skills_development.technical_growth === 'Moderate' ? 'bg-primary' :
                                  'bg-warning'
                                }`}>
                                  {careerProgression.analysis.skills_development.technical_growth}
                                </span>
                              </div>
                              <div className="col-12">
                                <small className="text-muted d-block">Leadership Growth</small>
                                <span className={`badge ${
                                  careerProgression.analysis.skills_development.leadership_growth === 'Strong' ? 'bg-success' :
                                  careerProgression.analysis.skills_development.leadership_growth === 'Moderate' ? 'bg-primary' :
                                  'bg-warning'
                                }`}>
                                  {careerProgression.analysis.skills_development.leadership_growth}
                                </span>
                              </div>
                              <div className="col-12">
                                <small className="text-muted d-block">Adaptability</small>
                                <span className={`badge ${
                                  careerProgression.analysis.skills_development.adaptability === 'High' ? 'bg-success' :
                                  careerProgression.analysis.skills_development.adaptability === 'Medium' ? 'bg-primary' :
                                  'bg-secondary'
                                }`}>
                                  {careerProgression.analysis.skills_development.adaptability}
                                </span>
                              </div>
                            </div>
                            <p className="small mb-0">{careerProgression.analysis.skills_development.analysis}</p>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Current Application Fit */}
                  {careerProgression.analysis?.current_application_fit && (
                    <div className="card border-0 bg-light mb-4">
                      <div className="card-body">
                        <h6 className="card-title text-primary mb-3">
                          <Briefcase size={16} className="me-1" />
                          Current Application Fit
                        </h6>
                        <div className="row g-3 mb-3">
                          <div className="col-md-3">
                            <small className="text-muted d-block">Role Alignment</small>
                            <span className={`badge ${
                              careerProgression.analysis.current_application_fit.role_alignment === 'Excellent' || careerProgression.analysis.current_application_fit.role_alignment === 'Good' ? 'bg-success' :
                              careerProgression.analysis.current_application_fit.role_alignment === 'Fair' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {careerProgression.analysis.current_application_fit.role_alignment}
                            </span>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Level Fit</small>
                            <span className="badge bg-info">
                              {careerProgression.analysis.current_application_fit.level_appropriateness}
                            </span>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Company Fit</small>
                            <span className={`badge ${
                              careerProgression.analysis.current_application_fit.company_fit === 'Excellent' || careerProgression.analysis.current_application_fit.company_fit === 'Good' ? 'bg-success' :
                              careerProgression.analysis.current_application_fit.company_fit === 'Fair' ? 'bg-warning' :
                              'bg-secondary'
                            }`}>
                              {careerProgression.analysis.current_application_fit.company_fit}
                            </span>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Success Likelihood</small>
                            <span className={`badge ${
                              careerProgression.analysis.current_application_fit.likelihood_of_success === 'Very High' || careerProgression.analysis.current_application_fit.likelihood_of_success === 'High' ? 'bg-success' :
                              careerProgression.analysis.current_application_fit.likelihood_of_success === 'Medium' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {careerProgression.analysis.current_application_fit.likelihood_of_success}
                            </span>
                          </div>
                        </div>
                        <p className="mb-0">{careerProgression.analysis.current_application_fit.analysis}</p>
                      </div>
                    </div>
                  )}

                  {/* Strengths and Concerns */}
                  <div className="row g-4 mb-4">
                    {/* Strengths */}
                    {careerProgression.analysis?.strengths && careerProgression.analysis.strengths.length > 0 && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light h-100">
                          <div className="card-body">
                            <h6 className="card-title text-success mb-3">
                              <Check size={16} className="me-1" />
                              Career Strengths
                            </h6>
                            <ul className="list-unstyled mb-0">
                              {careerProgression.analysis.strengths.map((strength, index) => (
                                <li key={index} className="mb-2">
                                  <Check size={16} className="text-success me-2" />
                                  {strength}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Concerns */}
                    {careerProgression.analysis?.concerns && careerProgression.analysis.concerns.length > 0 && (
                      <div className="col-md-6">
                        <div className="card border-0 bg-light h-100">
                          <div className="card-body">
                            <h6 className="card-title text-warning mb-3">
                              <X size={16} className="me-1" />
                              Areas of Concern
                            </h6>
                            <ul className="list-unstyled mb-0">
                              {careerProgression.analysis.concerns.map((concern, index) => (
                                <li key={index} className="mb-2">
                                  <X size={16} className="text-warning me-2" />
                                  {concern}
                                </li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Recommendations */}
                  {careerProgression.analysis?.recommendations && (
                    <div className="card border-0 bg-light mb-4">
                      <div className="card-body">
                        <h6 className="card-title text-primary mb-3">
                          <MessageSquare size={16} className="me-1" />
                          Recommendations
                        </h6>
                        <div className="row g-4">
                          {careerProgression.analysis.recommendations.for_recruiter && careerProgression.analysis.recommendations.for_recruiter.length > 0 && (
                            <div className="col-md-6">
                              <h6 className="text-primary mb-2">For Recruiter</h6>
                              <ul className="list-unstyled">
                                {careerProgression.analysis.recommendations.for_recruiter.map((rec, index) => (
                                  <li key={index} className="mb-2">
                                    <span className="text-primary me-2">•</span>
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {careerProgression.analysis.recommendations.for_candidate && careerProgression.analysis.recommendations.for_candidate.length > 0 && (
                            <div className="col-md-6">
                              <h6 className="text-info mb-2">For Candidate</h6>
                              <ul className="list-unstyled">
                                {careerProgression.analysis.recommendations.for_candidate.map((rec, index) => (
                                  <li key={index} className="mb-2">
                                    <span className="text-info me-2">•</span>
                                    {rec}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Overall Assessment */}
                  {careerProgression.analysis?.overall_assessment && (
                    <div className="card border-primary">
                      <div className="card-body">
                        <h6 className="card-title text-primary mb-3">
                          <Award size={16} className="me-1" />
                          Overall Assessment
                        </h6>
                        <div className="row g-3 mb-3">
                          <div className="col-md-3">
                            <small className="text-muted d-block">Career Maturity</small>
                            <span className={`badge ${
                              careerProgression.analysis.overall_assessment.career_maturity === 'High' ? 'bg-success' :
                              careerProgression.analysis.overall_assessment.career_maturity === 'Medium' ? 'bg-primary' :
                              'bg-secondary'
                            }`}>
                              {careerProgression.analysis.overall_assessment.career_maturity}
                            </span>
                          </div>
                          <div className="col-md-3">
                            <small className="text-muted d-block">Risk Level</small>
                            <span className={`badge ${
                              careerProgression.analysis.overall_assessment.risk_level === 'Low' ? 'bg-success' :
                              careerProgression.analysis.overall_assessment.risk_level === 'Medium' ? 'bg-warning' :
                              'bg-danger'
                            }`}>
                              {careerProgression.analysis.overall_assessment.risk_level}
                            </span>
                          </div>
                          <div className="col-md-6">
                            <small className="text-muted d-block">Potential Rating</small>
                            <span className={`badge ${
                              careerProgression.analysis.overall_assessment.potential_rating === 'Excellent' || careerProgression.analysis.overall_assessment.potential_rating === 'Good' ? 'bg-success' :
                              careerProgression.analysis.overall_assessment.potential_rating === 'Average' ? 'bg-primary' :
                              'bg-warning'
                            }`}>
                              {careerProgression.analysis.overall_assessment.potential_rating}
                            </span>
                          </div>
                        </div>
                        <p className="mb-0">{careerProgression.analysis.overall_assessment.summary}</p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowCareerProgressionModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <style>
        {`
          .markdown-content {
            font-family: inherit;
          }
          .markdown-content h1 {
            font-size: 1.75rem;
            margin-bottom: 1rem;
            font-weight: 600;
          }
          .markdown-content h2 {
            font-size: 1.5rem;
            margin-bottom: 0.875rem;
            font-weight: 600;
          }
          .markdown-content h3 {
            font-size: 1.25rem;
            margin-bottom: 0.75rem;
            font-weight: 600;
          }
          .markdown-content p {
            margin-bottom: 1rem;
            line-height: 1.6;
          }
          .markdown-content ul, .markdown-content ol {
            margin-bottom: 1rem;
            padding-left: 2rem;
          }
          .markdown-content li {
            margin-bottom: 0.5rem;
          }
          .markdown-content strong {
            font-weight: 600;
          }
          .markdown-content em {
            font-style: italic;
          }
          .markdown-content code {
            background-color: #f8f9fa;
            padding: 0.2em 0.4em;
            border-radius: 3px;
            font-size: 85%;
          }
          .markdown-content blockquote {
            border-left: 4px solid #e9ecef;
            padding-left: 1rem;
            margin-left: 0;
            color: #6c757d;
          }
          .markdown-content hr {
            margin: 2rem 0;
            border-top: 2px solid #e9ecef;
          }
          
          /* Timeline Styles */
          .timeline-container {
            position: relative;
          }
          
          .timeline-entry {
            position: relative;
          }
          
          .timeline-marker {
            position: relative;
            display: flex;
            flex-direction: column;
            align-items: center;
            min-height: 100%;
          }
          
          .timeline-dot {
            width: 16px;
            height: 16px;
            border-radius: 50%;
            border: 3px solid #fff;
            box-shadow: 0 0 0 2px #e9ecef;
            z-index: 2;
            flex-shrink: 0;
          }
          
          .timeline-line {
            width: 2px;
            background-color: #e9ecef;
            flex-grow: 1;
            min-height: 40px;
            margin-top: 8px;
          }
          
          .timeline-content {
            padding-bottom: 1rem;
          }
          
          .timeline-entry:last-child .timeline-line {
            display: none;
          }
          
          /* Technology Stack Styles */
          .technology-stack-content {
            font-family: inherit;
          }
          .technology-stack-content h1,
          .technology-stack-content h2,
          .technology-stack-content h3,
          .technology-stack-content h4,
          .technology-stack-content h5,
          .technology-stack-content h6 {
            color: #0d6efd;
            margin-bottom: 0.5rem;
            margin-top: 1rem;
          }
          .technology-stack-content h1:first-child,
          .technology-stack-content h2:first-child,
          .technology-stack-content h3:first-child,
          .technology-stack-content h4:first-child,
          .technology-stack-content h5:first-child,
          .technology-stack-content h6:first-child {
            margin-top: 0;
          }
          .technology-stack-content p {
            margin-bottom: 0.75rem;
            line-height: 1.5;
          }
          .technology-stack-content ul,
          .technology-stack-content ol {
            margin-bottom: 0.75rem;
            padding-left: 1.5rem;
          }
          .technology-stack-content li {
            margin-bottom: 0.25rem;
            line-height: 1.4;
          }
          .technology-stack-content strong {
            font-weight: 600;
            color: #212529;
          }
          .technology-stack-content em {
            font-style: italic;
            color: #6c757d;
          }
          .technology-stack-content code {
            background-color: #f8f9fa;
            color: #e83e8c;
            padding: 0.125rem 0.25rem;
            border-radius: 0.25rem;
            font-size: 87.5%;
            font-family: 'SFMono-Regular', Consolas, 'Liberation Mono', Menlo, Courier, monospace;
          }
        `}
      </style>
    </div>
  );
} 